import { type FC, useCallback, useState } from "react";
import { type MessageType } from "./Messages/Message";
import classNames from "classnames";
import { styled } from "@mui/material";

import { IconClickable, IconClickableType } from "./icon/IconClickable";
import { IconType } from "./icon/IconType";
import { useTranslation } from "react-i18next";
import { Alert } from "../m3/Alert/Alert";

export interface Props {
  msgType: MessageType;
  msgCount: number;
  onScrollToMessage: (index: number) => void;
  sticky?: boolean;
  isBulkUploadPreview?: boolean;
}

const CheckMessagesComponent: FC<Props & JSX.IntrinsicElements["div"]> = (props) => {
  const { msgType, className, onScrollToMessage, msgCount, sticky, isBulkUploadPreview, ...divProps } = props;
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(-1);

  const nextClick = useCallback(() => {
    let nextIndex = activeIndex + 1;
    if (nextIndex >= msgCount) {
      nextIndex = 0;
    }
    setActiveIndex(nextIndex);
    onScrollToMessage(nextIndex);
  }, [activeIndex, msgCount, onScrollToMessage]);

  const prevClick = useCallback(() => {
    let nextIndex = activeIndex - 1;
    if (nextIndex < 0) {
      nextIndex = msgCount - 1;
    }
    setActiveIndex(nextIndex);
    onScrollToMessage(nextIndex);
  }, [activeIndex, msgCount, onScrollToMessage]);

  if (activeIndex >= msgCount) {
    setActiveIndex(activeIndex - 1);
    return null;
  }

  const cls = classNames(
    className,
    !isBulkUploadPreview && "scha__check-messages",
    sticky && "scha__check-messages--sticky",
  );
  const msg = t(`component.checkMessages.${msgType}`) || "";
  const clickableType = msgType === "error" ? IconClickableType.ERROR : IconClickableType.INFO;

  if (msgCount === 0) {
    return null;
  }

  return (
    <div className={cls} {...divProps}>
      {isBulkUploadPreview ? (
        <AlertContainer className={"mr-auto"} severity={msgType}>
          <span>
            {`${msg} `}[{activeIndex + 1}/{msgCount}]
          </span>
          <ButtonContainer>
            <IconClickable
              type={IconType.ChevronDown}
              clickableType={clickableType}
              onIconClick={nextClick}
              disabled={msgCount !== 1 && activeIndex === msgCount - 1}
            />
            <IconClickable
              type={IconType.ChevronUp}
              clickableType={clickableType}
              onIconClick={prevClick}
              disabled={msgCount !== 1 && activeIndex <= 0}
            />
          </ButtonContainer>
        </AlertContainer>
      ) : (
        <>
          <Alert className={"mr-auto"} severity={msgType}>
            {msg}
          </Alert>
          <span className={"scha__check-messages__counter"}>
            {activeIndex + 1}/{msgCount}
          </span>
          <div>
            <IconClickable
              type={IconType.ChevronDown}
              onIconClick={nextClick}
              disabled={msgCount !== 1 && activeIndex === msgCount - 1}
            />
            <IconClickable
              type={IconType.ChevronUp}
              onIconClick={prevClick}
              disabled={msgCount !== 1 && activeIndex <= 0}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default CheckMessagesComponent;

const AlertContainer = styled(Alert)({
  width: "100%",
  maxWidth: "unset",

  [`.MuiAlert-message`]: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
  },
});

const ButtonContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});
